// BackendPageForCS.js — the "Complex Systems" page (one case-study block per system).
//
// Reads its data from <ConfigContext> (App.js fetches the WHOLE website dict
// once at first load — navigating here makes no backend call). Content lives in
// website_items_dev/4_complex_systems/complex_systems.yaml — edit + refresh,
// no rebuild. Items appear when a yaml key matches an image in graphic_items/.
//
// Layout per item (campaign slides 2-4 of the End-To-End carousel):
//   title (centered) + orange subtitle (orange via <...> markup in the yaml)
//   app screenshot — same height for ALL systems, width follows the image's own ratio;
//     scalable in % via yaml format.images / per-item image.format_override (--shot-scale vars)
//   three INSIDE BOXES: PROBLEM (soft red) | W4U SOLUTION (orange) | RESULT (soft green) —
//     each box = label + short description + its ELEMENTS listed one by one (yaml list, in order)
//     · desktop: 3-column grid
//     · mobile: a slow slider — user-swipeable, auto-advances circularly; timings come from
//       the yaml 'mobile_slider' block; the next box peeks from the right edge as a hint
//   closing quote (the item's 'header' — same line the homepage box shows)
//   takeaway chips (list, in yaml order; gap from the yaml 'takeaways_spacing' block)
//
// Each case <article> carries id=<yaml tag> so the homepage boxes can deep-link
// to /complex-systems#<tag> (scroll handled by the hash effect below).

import React, { useContext, useEffect, useRef, useState } from 'react';
import { ConfigContext } from '../../App';
import { OrangeButton } from '../../myReactLib/BasicItems';
import {
  Navigation,
  Footer,
  getText,
  getStyle,
  getBottomMargin,
  RT,
} from '../../myReactLib/MainBlocks';

// fallback slider timings, used when the yaml 'mobile_slider' block is missing/empty
const INSIDE_BOX_AUTOSLIDE_MS = 5000;
const INSIDE_BOX_PAUSE_MS = 9000;

// parse a yaml percentage ("100%" = default size) into a scale factor for the CSS calc
function shotScale(pct) {
  const n = parseFloat(String(pct || '').replace('%', ''));
  return Number.isFinite(n) ? n / 100 : 1;
}

function BackendPageForCS() {
  const { config, homepageData, complexSystemsData, contactData } = useContext(ConfigContext);

  // deep-link support: /complex-systems#<tag> scrolls to that case study
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!homepageData) return null;
  const menuBar = homepageData.menu_bar;
  const prefix  = config.PREFIX;
  const data    = complexSystemsData;

  // 'page.items' = same items as the homepage boxes, but with the page-only fields
  // (inside boxes + takeaways) and the yaml 'format_page' overlay applied.
  const page  = (data && data.page) || {};
  const items = page.items || {};

  return (
    <div className="w4u-page">
      <Navigation menuBar={menuBar} prefix={prefix} />
      <main className="w4u-main">
        {!data && <div className="w4u-loading">Loading…</div>}
        {data && (
          <>
            <CsHeader header={page.page_header} />
            <section className="w4u-cs__list">
              {Object.entries(items).map(([tag, item]) => (
                <CsCaseStudy key={tag} tag={tag} item={item} labels={page.labels} mobileSlider={page.mobile_slider} takeawaysSpacing={page.takeaways_spacing} />
              ))}
            </section>
            <CsCta cta={page.cta} />
          </>
        )}
      </main>
      <Footer menuBar={menuBar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

// ----- Page header -----

const CsHeader = ({ header }) => {
  if (!header) return null;
  return (
    <section className="w4u-cs__header" style={getBottomMargin(header.bottom_margin)}>
      <h1 className="w4u-cs__title" style={getStyle(header.title)}>{RT(header.title)}</h1>
      {header.subtitle && (
        <p className="w4u-cs__subtitle" style={getStyle(header.subtitle)}>{RT(header.subtitle)}</p>
      )}
      {header.subtitle_2 && (
        <p className="w4u-cs__subtitle2" style={getStyle(header.subtitle_2)}>{RT(header.subtitle_2)}</p>
      )}
    </section>
  );
};

// ----- One case-study block -----

const CsCaseStudy = ({ tag, item, labels = {}, mobileSlider, takeawaysSpacing }) => {
  const takeaways = Array.isArray(item.takeaways) ? item.takeaways : [];

  // screenshot scale (yaml %, '100%' = the CSS base size) → CSS vars consumed by index.css
  const imageSize = item.image_size || {};
  const shotStyle = { '--shot-scale': shotScale(imageSize.desktop), '--shot-scale-mobile': shotScale(imageSize.mobile) };

  // yaml-driven gap between the takeaway chips (desktop inline; mobile via the --gap-mobile rule)
  const tkSpacing = takeawaysSpacing || {};
  const tkStyle = {};
  if (tkSpacing.desktop) tkStyle.gap = tkSpacing.desktop;
  if (tkSpacing.mobile)  tkStyle['--gap-mobile'] = tkSpacing.mobile;

  return (
    <article id={tag} className="w4u-cs__case" style={getBottomMargin(item.bottom_margin)}>
      <h2 className="w4u-cs__case-title" style={getStyle(item.title)}>{RT(item.title)}</h2>
      {item.subtitle && (
        <p className="w4u-cs__case-subtitle" style={getStyle(item.subtitle)}>{RT(item.subtitle)}</p>
      )}
      {item.image_path && (
        <div className="w4u-cs__shot-wrap">
          <img src={item.image_path} alt={getText(item.title)} className="w4u-cs__shot" style={shotStyle} />
        </div>
      )}
      <CsInsideBoxes item={item} labels={labels} mobileSlider={mobileSlider} />
      {item.header && (
        <blockquote className="w4u-cs__quote" style={getStyle(item.header)}>{RT(item.header)}</blockquote>
      )}
      {takeaways.length > 0 && (
        <ul className="w4u-cs__takeaways" style={Object.keys(tkStyle).length ? tkStyle : undefined}>
          {takeaways.map((tk, i) => (
            <li key={i} className="w4u-cs__takeaway-chip" style={getStyle(tk)}>{RT(tk)}</li>
          ))}
        </ul>
      )}
    </article>
  );
};

// ----- One inside box (label + description + elements) -----
// boxKey ('problem' | 'solution' | 'result') drives the per-box coloring:
// soft red / orange / soft green (classes defined in index.css).

const CsInsideBox = ({ label, box, boxKey }) => (
  <div className={`w4u-cs__column w4u-cs__column--${boxKey}`}>
    <span
      className={`w4u-cs__column-label w4u-cs__column-label--${boxKey}`}
      style={getStyle(label)}
    >{RT(label)}</span>
    {box.description && (
      <p className="w4u-cs__column-desc" style={getStyle(box.description)}>{RT(box.description)}</p>
    )}
    {Array.isArray(box.elements) && box.elements.length > 0 && (
      <ul className="w4u-cs__elements">
        {box.elements.map((el, i) => (
          <li key={i} className="w4u-cs__element" style={getStyle(el)}>{RT(el)}</li>
        ))}
      </ul>
    )}
  </div>
);

// ----- The three inside boxes: grid on desktop, slow circular slider on mobile -----

const CsInsideBoxes = ({ item, labels, mobileSlider }) => {
  // assemble the boxes present on this item (problem / solution / result, in order)
  const boxes = [];
  if (item.problem)  boxes.push({ key: 'problem',  label: labels.problem,  box: item.problem });
  if (item.solution) boxes.push({ key: 'solution', label: labels.solution, box: item.solution });
  if (item.result)   boxes.push({ key: 'result',   label: labels.result,   box: item.result });

  // slider timings from the yaml 'mobile_slider' block (seconds → ms; fallback to the constants)
  const slider = mobileSlider || {};
  const autoslideMs = (parseFloat(slider.autoslide_seconds) > 0 ? parseFloat(slider.autoslide_seconds) * 1000 : INSIDE_BOX_AUTOSLIDE_MS);
  const pauseMs = (parseFloat(slider.pause_seconds) > 0 ? parseFloat(slider.pause_seconds) * 1000 : INSIDE_BOX_PAUSE_MS);

  // mobile detection (same 768px breakpoint as the CSS)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // slider state (mobile only): current slide + a "paused until" timestamp that
  // user interactions push forward, so the auto-advance never fights the user
  const trackRef = useRef(null);
  const pausedUntil = useRef(0);
  const [index, setIndex] = useState(0);

  // auto-advance circularly every few seconds (unless recently touched)
  useEffect(() => {
    if (!isMobile || boxes.length <= 1) return;
    const timer = setInterval(() => {
      if (Date.now() < pausedUntil.current) return;
      setIndex((i) => (i + 1) % boxes.length);
    }, autoslideMs);
    return () => clearInterval(timer);
  }, [isMobile, boxes.length, autoslideMs]);

  // scroll the track to the active slide whenever the index changes
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !track.children[index]) return;
    const slide = track.children[index];
    track.scrollTo({ left: slide.offsetLeft - track.children[0].offsetLeft, behavior: 'smooth' });
  }, [index, isMobile]);

  // user interactions: pause the auto-advance and re-sync the index to what's visible
  const pause = () => { pausedUntil.current = Date.now() + pauseMs; };
  const onUserScroll = () => {
    if (Date.now() >= pausedUntil.current) return;   // programmatic scroll → ignore
    const track = trackRef.current;
    if (!track || !track.children.length) return;
    const slideStride = (track.children[1] ? track.children[1].offsetLeft - track.children[0].offsetLeft : track.children[0].offsetWidth) || 1;  // slide width + gap
    const nearest = Math.round(track.scrollLeft / slideStride);
    setIndex(Math.max(0, Math.min(boxes.length - 1, nearest)));
  };

  if (boxes.length === 0) return null;

  // desktop: plain 3-column grid
  if (!isMobile) {
    return (
      <div className="w4u-cs__columns">
        {boxes.map((b) => <CsInsideBox key={b.key} label={b.label} box={b.box} boxKey={b.key} />)}
      </div>
    );
  }

  // mobile: swipeable snap track + dots, auto-advancing circularly. The slides are slightly
  // narrower than the viewport so the next box peeks from the right edge (plus a soft fade
  // overlay on the viewport) — hints the user that there is more to swipe.
  return (
    <div className="w4u-cs__columns-slider">
      <div className="w4u-cs__columns-viewport">
        <div
          className="w4u-cs__columns-track"
          ref={trackRef}
          onTouchStart={pause}
          onPointerDown={pause}
          onWheel={pause}
          onScroll={onUserScroll}
        >
          {boxes.map((b) => <CsInsideBox key={b.key} label={b.label} box={b.box} boxKey={b.key} />)}
        </div>
      </div>
      <div className="w4u-cs__columns-dots">
        {boxes.map((b, i) => (
          <button
            key={b.key}
            type="button"
            className={`w4u-cs__columns-dot w4u-cs__columns-dot--${b.key}` + (i === index ? ' w4u-cs__columns-dot--active' : '')}
            aria-label={`Show ${b.key} box`}
            onClick={() => { pause(); setIndex(i); }}
          />
        ))}
      </div>
    </div>
  );
};

// ----- CTA -----

const CsCta = ({ cta }) => {
  if (!cta) return null;
  return (
    <section className="w4u-cs__cta" style={getBottomMargin(cta.bottom_margin)}>
      {cta.line_1 && <p className="w4u-cs__cta-line" style={getStyle(cta.line_1)}>{RT(cta.line_1)}</p>}
      {cta.punch_line && <p className="w4u-cs__cta-punch" style={getStyle(cta.punch_line)}>{RT(cta.punch_line)}</p>}
      <OrangeButton to="/contact" variant="hero" style={getStyle(cta.button_text)}>
        {getText(cta.button_text) ? RT(cta.button_text) : 'Contact us'}
      </OrangeButton>
    </section>
  );
};

export default BackendPageForCS;
